import React from 'react';
import { Spinner } from 'components/UI';
import moment from 'moment-timezone';
import styles from './TransactionsDetailTable.module.scss';
import { TransactionsReport } from 'model/TransactionsReport';
import { useMasterData } from 'hooks';
import { InvoiceNumberLink } from 'components/Payments/InvoiceNumberLink';

interface TransactionsDetailTableProps {
    loading: boolean;
    report: TransactionsReport | null;
    onOpenInvoice?: (invoiceId: number, marketplaceId?: number) => void;
}

export const TransactionsDetailTable: React.FunctionComponent<TransactionsDetailTableProps> = props => {
    const { timeZones } = useMasterData();

    const formatCurrency = (amount: number) =>
        amount < 0 ? `($${Math.abs(amount).toFixed(2)})` : `$${amount.toFixed(2)}`;
    const formatPercentage = (rate: number) => `${rate.toFixed(3)}%`;
    const formatDate = (date: string) => moment(date).format('MM/DD/YYYY');

    const formatTimezone = (timeZoneCode: string) => {
        return timeZones.find(timeZone => timeZone.id === timeZoneCode)?.name || timeZoneCode || '—';
    };

    return (
        <div className={styles.root}>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead className={styles.header}>
                        <tr>
                            <th className={styles.headerCell}>Transaction Date</th>
                            <th className={styles.headerCell}>Time</th>
                            <th className={styles.headerCell}>Timezone</th>
                            <th className={styles.headerCell}>Gross Sales</th>
                            <th className={styles.headerCell}>Discounts</th>
                            <th className={styles.headerCell}>Refunds</th>
                            <th className={styles.headerCell}>Net Sales</th>
                            <th className={styles.headerCell}>Tax</th>
                            <th className={styles.headerCell}>Tip</th>
                            <th className={styles.headerCell}>Service Charges</th>
                            <th className={styles.headerCell}>Total Collected</th>
                            <th className={styles.headerCell}>Fees</th>
                            <th className={styles.headerCell}>Net Total</th>
                            <th className={styles.headerCell}>Event Type</th>
                            <th className={styles.headerCell}>Transaction Status</th>
                            <th className={styles.headerCell}>Payment Type</th>
                            <th className={styles.headerCell}>Card Payment Method</th>
                            <th className={styles.headerCell}>Transaction ID</th>
                            <th className={styles.headerCell}>Invoice</th>
                            <th className={styles.headerCell}>Appointment ID</th>
                            <th className={styles.headerCell}>Customer ID</th>
                            <th className={styles.headerCell}>Customer Name</th>
                            <th className={styles.headerCell}>Pet Name</th>
                            <th className={styles.headerCell}>Fee Percentage Rate</th>
                            <th className={styles.headerCell}>Fee Fixed Rate</th>
                            <th className={styles.headerCell}>Location</th>
                        </tr>
                    </thead>

                    <tbody className={styles.body}>
                        {props.loading ? (
                            <tr>
                                <td
                                    colSpan={26}
                                    className={styles.loadingCell}
                                >
                                    <Spinner className={styles.spinner} />
                                </td>
                            </tr>
                        ) : (
                            props.report &&
                            props.report.rows?.map((item, index) => (
                                <tr
                                    key={index}
                                    className={styles.row}
                                >
                                    <td className={styles.cell}>{formatDate(item.transactionDate)}</td>
                                    <td className={styles.cell}>{item.time}</td>
                                    <td className={styles.cell}>{formatTimezone(item.timezone)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.grossSales)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.discounts)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.refunds)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.netSales)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.tax)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.tip)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.serviceCharges)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.totalCollected)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.fees)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.netTotal)}</td>
                                    <td className={styles.cell}>{item.eventType}</td>
                                    <td className={styles.cell}>
                                        <span
                                            className={`${styles.status} ${
                                                styles[item.transactionStatus.toLowerCase()]
                                            }`}
                                        >
                                            {item.transactionStatus}
                                        </span>
                                    </td>
                                    <td className={styles.cell}>{item.paymentType}</td>
                                    <td className={styles.cell}>{item.cardPaymentMethod}</td>
                                    <td className={styles.cell}>{item.transactionId}</td>
                                    <td className={styles.cell}>
                                        <InvoiceNumberLink
                                            invoiceId={item.invoiceId}
                                            invoiceNumber={item.invoiceNumber ?? item.invoiceId ?? '—'}
                                            onClick={invoiceId =>
                                                props.onOpenInvoice?.(invoiceId, item.marketplaceId ?? undefined)
                                            }
                                        />
                                    </td>
                                    <td className={styles.cell}>{item.appointmentId}</td>
                                    <td className={styles.cell}>{item.customerId}</td>
                                    <td className={styles.cell}>{item.customerName}</td>
                                    <td className={styles.cell}>{item.petName}</td>
                                    <td className={styles.cell}>{formatPercentage(item.feePercentageRate)}</td>
                                    <td className={styles.cell}>{formatCurrency(item.feeFixedRate)}</td>
                                    <td className={styles.cell}>{item.location}</td>
                                </tr>
                            ))
                        )}
                    </tbody>

                    {!props.loading && props.report && props.report.rows.length > 0 && (
                        <tfoot>
                            <tr className={styles.totalRow}>
                                <td
                                    colSpan={3}
                                    className={styles.cell}
                                >
                                    Period total
                                    <span className={styles.totalHint}>Includes all pages</span>
                                </td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.grossSales)}</td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.discounts)}</td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.refunds)}</td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.netSales)}</td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.tax)}</td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.tip)}</td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.serviceCharges)}</td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.totalCollected)}</td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.fees)}</td>
                                <td className={styles.cell}>{formatCurrency(props.report.totals.netTotal)}</td>
                                <td
                                    colSpan={13}
                                    className={styles.cell}
                                />
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};

export default TransactionsDetailTable;
